import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import Swal from "sweetalert2";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2";
import "./RaiseComplaint.css";
import "./UserManagement.css";
import "./AddEditUser.css";
import "./DynamicApprovalConfiguration.css";
import { approvalWorkflowAPI, masterDataAPI, categoryMasterAPI, productTypeMasterAPI, productMasterAPI } from "../services/api";

const DynamicApprovalConfiguration = () => {
  const navigate = useNavigate();
  const { workflowCode } = useParams();
  const isEditMode = !!workflowCode && window.location.pathname.includes("/edit/");
  const isConfigureMode = !!workflowCode && window.location.pathname.includes("/configure/");
  const isAddMode = window.location.pathname.includes("/add");
  
  const [workflows, setWorkflows] = useState([]);
  const [workflowLevels, setWorkflowLevels] = useState({});
  const [loading, setLoading] = useState(false);
  const [showWorkflowForm, setShowWorkflowForm] = useState(isAddMode || isEditMode);
  const [showLevelForm, setShowLevelForm] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [editingLevel, setEditingLevel] = useState(null);
  const [expandedWorkflows, setExpandedWorkflows] = useState({});
  const [selectedWorkflow, setSelectedWorkflow] = useState(isConfigureMode ? workflowCode : null);

  // Form states
  const [workflowForm, setWorkflowForm] = useState({
    workflowCode: "",
    workflowName: "",
    srTypeCode: "",
    marketCode: "",
    catgCode: "",
    isActive: true,
  });

  const [levelForm, setLevelForm] = useState({
    approvalLevelCode: "",
    levelSequence: 1,
    approvalType: "Required", // Required, Optional, Conditional
    minValue: "",
    maxValue: "",
    currency: "INR",
    plantCode: "",
    userTypeCode: "",
    isPlantQCRequired: false,
    isActive: true,
  });

  // Dropdown options
  const [srTypeOptions, setSrTypeOptions] = useState([]);
  const [plantOptions, setPlantOptions] = useState([]);
  const [userTypeOptions, setUserTypeOptions] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [categories, setCategories] = useState([]);

  const tableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const marketSelectRef = useRef(null);
  const catgSelectRef = useRef(null);
  const srTypeSelectRef = useRef(null);
  const isSettingInitialMarketRef = useRef(false);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  // Load initial data
  useEffect(() => {
    if (isAddMode || isEditMode || isConfigureMode) {
      loadMarkets();
      if (isAddMode) {
        loadNextWorkflowCode();
      }
      if (isEditMode || isConfigureMode) {
        loadWorkflowForEdit();
      }
      if (isConfigureMode) {
        loadWorkflowLevels(workflowCode);
      }
    } else {
      loadWorkflows();
      loadMarkets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowCode, isEditMode, isConfigureMode, isAddMode]);

  // Initialize DataTable
  useEffect(() => {
    if (!tableRef.current || workflows.length === 0) return;

    if (dataTableInstanceRef.current) {
      dataTableInstanceRef.current.destroy();
      dataTableInstanceRef.current = null;
    }

    try {
      if (!$.fn.DataTable) {
        DataTable($);
      }

      dataTableInstanceRef.current = $(tableRef.current).DataTable({
        data: workflows.map((workflow, index) => ({ ...workflow, rowIndex: index + 1 })),
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [10, 25, 50, 100],
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        columns: [
          {
            data: "rowIndex",
            title: "SNO.",
            orderable: false,
            render: (data, type, row) => {
              if (type === "display") {
                return `<span>${data || "-"}</span>`;
              }
              return data || "";
            },
          },
          {
            data: "workflowName",
            title: "Workflow Name",
            render: (data, type, row) => {
              if (type === "display") {
                return `<span class="workflow-name">${data || "-"}</span>`;
              }
              return data || "";
            },
          },
          {
            data: "marketCode",
            title: "Market",
            render: (data, type, row) => {
              if (type === "display") {
                const market = markets.find((m) => (m.marketCode || m.MarketCode) === data);
                return market ? (market.marketName || market.MarketName) : data || "-";
              }
              return data || "";
            },
          },
          {
            data: "catgCode",
            title: "Product Category",
            render: (data, type, row) => {
              if (type === "display") {
                const category = categories.find((c) => (c.catgCode || c.CatgCode) === data);
                return category ? (category.catgName || category.CatgName) : data || "-";
              }
              return data || "";
            },
          },
          {
            data: "srTypeCode",
            title: "SR Type",
            render: (data, type, row) => {
              if (type === "display") {
                const srTypeCode = data || row.SRTYPECODE || "";
                const srType = srTypeOptions.find((st) => st.id === srTypeCode);
                return srType ? srType.text : srTypeCode || "-";
              }
              return data || row.SRTYPECODE || "";
            },
          },
          {
            data: "levelCount",
            title: "Levels",
            render: (data, type, row) => {
              if (type === "display") {
                return `<span>${data !== undefined ? data : (row.LevelCount !== undefined ? row.LevelCount : 0)}</span>`;
              }
              return data !== undefined ? data : (row.LevelCount !== undefined ? row.LevelCount : 0);
            },
          },
          {
            data: "createdOn",
            title: "Created On",
            render: (data, type, row) => {
              if (type === "display") {
                const date = data || row.CreatedOn || row.createdOn;
                if (date) {
                  try {
                    const dateObj = new Date(date);
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const year = dateObj.getFullYear();
                    const hours = String(dateObj.getHours()).padStart(2, '0');
                    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                    return `${day}-${month}-${year} ${hours}:${minutes}`;
                  } catch (e) {
                    return date;
                  }
                }
                return "-";
              }
              return data || row.CreatedOn || row.createdOn || "";
            },
          },
          {
            data: "modifiedOn",
            title: "Modified On",
            render: (data, type, row) => {
              if (type === "display") {
                const date = data || row.ModifiedOn || row.modifiedOn;
                if (date) {
                  try {
                    const dateObj = new Date(date);
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const year = dateObj.getFullYear();
                    const hours = String(dateObj.getHours()).padStart(2, '0');
                    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                    return `${day}-${month}-${year} ${hours}:${minutes}`;
                  } catch (e) {
                    return date;
                  }
                }
                return "-";
              }
              return data || row.ModifiedOn || row.modifiedOn || "";
            },
          },
          {
            data: "isActive",
            title: "Status",
            render: (data, type, row) => {
              if (type === "display") {
                const isActive = data !== undefined ? data : (row.IsActive !== undefined ? row.IsActive : true);
                return `<span class="badge ${isActive ? "badge-success" : "badge-danger"}">${
                  isActive ? "Active" : "Inactive"
                }</span>`;
              }
              return data !== undefined ? data : (row.IsActive !== undefined ? row.IsActive : true);
            },
          },
          {
            data: null,
            title: "Action",
            orderable: false,
            render: (data, type, row) => {
              if (type === "display") {
                return `
                  <div class="action-buttons">
                    <button class="btn btn-sm btn-levels" data-workflow-code="${row.workflowCode}" title="Manage Levels">
                      <i class="material-icons">list</i>
                    </button>
                    <button class="btn btn-sm btn-edit" data-workflow-code="${row.workflowCode}" title="Edit">
                      <i class="material-icons">edit</i>
                    </button>
                    <button class="btn btn-sm btn-delete" data-workflow-code="${row.workflowCode}" title="Delete">
                      <i class="material-icons">delete</i>
                    </button>
                  </div>
                `;
              }
              return "";
            },
          },
        ],
        language: {
          emptyTable: "No workflows found",
          zeroRecords: "No matching workflows found",
        },
      });

      // Attach event handlers
      $(tableRef.current).on("click", ".btn-edit", function () {
        const workflowCode = $(this).data("workflow-code");
        handleEditWorkflow(workflowCode);
      });

      $(tableRef.current).on("click", ".btn-delete", function () {
        const workflowCode = $(this).data("workflow-code");
        handleDeleteWorkflow(workflowCode);
      });

      $(tableRef.current).on("click", ".btn-levels", function () {
        const workflowCode = $(this).data("workflow-code");
        handleManageLevels(workflowCode);
      });
    } catch (error) {
      console.error("Error initializing DataTable:", error);
    }

    return () => {
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
    };
  }, [workflows, markets, categories, srTypeOptions]);

  // Initialize Select2 for Market dropdown
  useEffect(() => {
    if (!showWorkflowForm) return;
    if (marketSelectRef.current && markets.length > 0) {
      const timer = setTimeout(() => {
        const $select = $(marketSelectRef.current);
        if (!$select.length) return;
        
        const isSelect2Initialized = $select.data("select2");
        const shouldSetValue = workflowForm.marketCode && workflowForm.marketCode !== "0000";
        const currentVal = $select.val();
        
        if (isSelect2Initialized) {
          if (shouldSetValue && currentVal !== workflowForm.marketCode) {
            const hasCategory = workflowForm.catgCode && workflowForm.catgCode !== "0000";
            if (hasCategory) {
              isSettingInitialMarketRef.current = true;
            }
            $select.val(workflowForm.marketCode).trigger("change");
          }
          return;
        }
        
        $select.select2({
          width: "100%",
          placeholder: "Select Market",
          allowClear: false,
        });
        
        $select.on("change", function (e) {
          const selectedMarketCode = e.target.value || "0000";
          const isInitialLoad = isSettingInitialMarketRef.current;
          
          setWorkflowForm((prev) => ({ 
            ...prev, 
            marketCode: selectedMarketCode,
            catgCode: isInitialLoad ? prev.catgCode : "0000",
            srTypeCode: ""
          }));
          
          isSettingInitialMarketRef.current = false;
          
          if (selectedMarketCode && selectedMarketCode !== "0000") {
            loadCategories(selectedMarketCode);
          }
        });
        
        if (shouldSetValue) {
          setTimeout(() => {
            if ($select.val() !== workflowForm.marketCode) {
              isSettingInitialMarketRef.current = true;
              $select.val(workflowForm.marketCode).trigger("change");
            }
          }, 100);
        }
      }, 150);
      
      return () => {
        clearTimeout(timer);
        if (marketSelectRef.current) {
          const $select = $(marketSelectRef.current);
          if ($select.data("select2")) {
            try {
              $select.off("change");
              $select.select2("destroy");
            } catch (e) {
              // Ignore cleanup errors
            }
          }
        }
      };
    }
  }, [markets, workflowForm.marketCode, showWorkflowForm]);

  // Initialize Select2 for Category dropdown
  useEffect(() => {
    if (!showWorkflowForm) return;
    if (!catgSelectRef.current) return;
    
    const timer = setTimeout(() => {
      const $select = $(catgSelectRef.current);
      if (!$select.length) return;
      
      const isDisabled = !workflowForm.marketCode || workflowForm.marketCode === "0000";
      const isSelect2Initialized = $select.data("select2");
      
      if (isSelect2Initialized) {
        try {
          $select.off("change");
          $select.select2("destroy");
        } catch (e) {
          console.warn("Error destroying Select2:", e);
        }
      }
      
      $select.prop("disabled", isDisabled);
      
      $select.select2({
        width: "100%",
        placeholder: isDisabled ? "Please select Market first" : "Select Category",
        allowClear: false,
      });
      
      $select.on("change", function (e) {
        setWorkflowForm((prev) => ({ ...prev, catgCode: e.target.value || "0000" }));
      });
      
      setTimeout(() => {
        if (workflowForm.catgCode && workflowForm.catgCode !== "0000" && !isDisabled && categories.length > 0) {
          const categoryExists = categories.some(
            (cat) => (cat.catgCode || cat.CatgCode) === workflowForm.catgCode
          );
          if (categoryExists) {
            $select.val(workflowForm.catgCode).trigger("change.select2");
          } else {
            $select.val("0000").trigger("change.select2");
            if (workflowForm.catgCode !== "0000") {
              setWorkflowForm((prev) => ({ ...prev, catgCode: "0000" }));
            }
          }
        } else {
          $select.val("0000").trigger("change.select2");
        }
      }, 100);
    }, 200);
    
    return () => {
      clearTimeout(timer);
      if (catgSelectRef.current) {
        const $select = $(catgSelectRef.current);
        if ($select.data("select2")) {
          try {
            $select.off("change");
            $select.select2("destroy");
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      }
    };
  }, [categories, workflowForm.catgCode, workflowForm.marketCode, showWorkflowForm]);

  // Initialize Select2 for SR Type dropdown
  useEffect(() => {
    if (!showWorkflowForm) return;
    if (!srTypeSelectRef.current) return;
    
    const timer = setTimeout(() => {
      const $select = $(srTypeSelectRef.current);
      if (!$select.length) return;
      
      const isSelect2Initialized = $select.data("select2");
      
      if (isSelect2Initialized) {
        try {
          $select.off("change");
          $select.select2("destroy");
        } catch (e) {
          console.warn("Error destroying Select2:", e);
        }
      }
      
      $select.select2({
        width: "100%",
        placeholder: "Select SR Type",
        allowClear: false,
      });
      
      $select.on("change", function (e) {
        setWorkflowForm((prev) => ({ ...prev, srTypeCode: e.target.value || "" }));
      });
      
      setTimeout(() => {
        if (workflowForm.srTypeCode && srTypeOptions.length > 0) {
          const srTypeExists = srTypeOptions.some(
            (st) => st.id === workflowForm.srTypeCode
          );
          if (srTypeExists) {
            $select.val(workflowForm.srTypeCode).trigger("change.select2");
          } else {
            $select.val("").trigger("change.select2");
            if (workflowForm.srTypeCode) {
              setWorkflowForm((prev) => ({ ...prev, srTypeCode: "" }));
            }
          }
        } else {
          $select.val("").trigger("change.select2");
        }
      }, 100);
    }, 200);
    
    return () => {
      clearTimeout(timer);
      if (srTypeSelectRef.current) {
        const $select = $(srTypeSelectRef.current);
        if ($select.data("select2")) {
          try {
            $select.off("change");
            $select.select2("destroy");
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      }
    };
  }, [srTypeOptions, workflowForm.srTypeCode, showWorkflowForm]);

  const loadNextWorkflowCode = async () => {
    try {
      const response = await approvalWorkflowAPI.getNextCode();
      if (response?.success && response.data) {
        setWorkflowForm((prev) => ({ ...prev, workflowCode: response.data }));
      }
    } catch (error) {
      console.error("Error loading next workflow code:", error);
    }
  };

  const loadMarkets = async () => {
    try {
      const response = await masterDataAPI.getMarkets("", 1, 100);
      if (response?.success) {
        // Check different possible response structures
        if (response.data?.data && Array.isArray(response.data.data)) {
          setMarkets(response.data.data);
        } else if (Array.isArray(response.data)) {
          setMarkets(response.data);
        } else if (response.data?.markets && Array.isArray(response.data.markets)) {
          setMarkets(response.data.markets);
        } else {
          setMarkets([]);
        }
      } else if (Array.isArray(response?.data)) {
        setMarkets(response.data);
      } else {
        setMarkets([]);
      }
    } catch (e) {
      console.error("Error loading markets:", e);
      setMarkets([]);
    }
  };

  const loadCategories = async (marketCode) => {
    try {
      if (!marketCode || marketCode === "0000") {
        setCategories([]);
        return;
      }
      
      const response = await productMasterAPI.getCategories(true, marketCode);
      if (response?.success && response.data) {
        setCategories(Array.isArray(response.data) ? response.data : []);
      } else if (response?.data) {
        setCategories(Array.isArray(response.data) ? response.data : []);
      } else {
        setCategories([]);
      }
    } catch (e) {
      console.error("Error loading categories:", e);
      setCategories([]);
    }
  };

  // Load SR Types - load all SR Types for grid display and form
  useEffect(() => {
    const loadSRTypes = async () => {
      try {
        // Always load all SR Types (null = all SR Types) for grid display
        const srTypesRes = await masterDataAPI.getSRTypes(null, true);
        
        if (srTypesRes?.success) {
          // Check if data is directly an array
          let srTypes = [];
          if (Array.isArray(srTypesRes.data)) {
            srTypes = srTypesRes.data;
          } else if (srTypesRes.data && Array.isArray(srTypesRes.data)) {
            srTypes = srTypesRes.data;
          } else if (srTypesRes.data?.SRTypes && Array.isArray(srTypesRes.data.SRTypes)) {
            srTypes = srTypesRes.data.SRTypes;
          }
          
          if (srTypes.length > 0) {
            const mappedOptions = srTypes.map((st) => {
              const code = st.SRTypeCode || st.SRTYPECODE || st.srTypeCode || st.SrTypeCode || "";
              const name = st.Name || st.NAME || st.name || "";
              return { id: code, text: name };
            }).filter((st) => st.id && st.text);
            
            setSrTypeOptions(mappedOptions);
          } else {
            setSrTypeOptions([]);
          }
        } else {
          setSrTypeOptions([]);
        }
      } catch (error) {
        console.error("Error loading SR Types:", error);
        setSrTypeOptions([]);
      }
    };
    
    // Load SR Types on component mount (for grid display)
    loadSRTypes();
  }, []);

  // Load plants (one-time load)
  useEffect(() => {
    const loadPlants = async () => {
      try {
        const plantsRes = await masterDataAPI.getPlants();
        if (plantsRes?.success && plantsRes.data) {
          const plants = Array.isArray(plantsRes.data) ? plantsRes.data : [];
          setPlantOptions(
            plants.map((p) => ({
              id: p.plantCode || p.PlantCode || "",
              text: p.plantName || p.PlantName || "",
            }))
            .filter((p) => p.id && p.text)
          );
        }
      } catch (error) {
        console.error("Error loading plants:", error);
      }
    };
    loadPlants();
  }, []);

  // Load user types (static list)
  useEffect(() => {
    setUserTypeOptions([
      { id: "ADMIN", text: "Admin" },
      { id: "APPROVER", text: "Approver" },
      { id: "SE", text: "Sales Executive" },
      { id: "PLANT_QC", text: "Plant QC" },
    ]);
  }, []);

  // Load categories when market code changes
  useEffect(() => {
    if (workflowForm.marketCode && workflowForm.marketCode !== "0000") {
      loadCategories(workflowForm.marketCode);
    } else {
      setCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowForm.marketCode]);


  const loadWorkflowForEdit = async () => {
    try {
      setLoading(true);
      const response = await approvalWorkflowAPI.get(workflowCode);
      if (response?.success && response.data) {
        const workflow = response.data;
        setEditingWorkflow(workflow);
        const marketCode = workflow.marketCode || workflow.MARKETCODE || "";
        const catgCode = workflow.catgCode || workflow.CATGCODE || "";
        const srTypeCode = workflow.srTypeCode || workflow.SRTYPECODE || "";
        
        setWorkflowForm({
          workflowCode: workflow.workflowCode || workflow.WorkflowCode || "",
          workflowName: workflow.workflowName || workflow.WorkflowName || "",
          srTypeCode: srTypeCode,
          marketCode: marketCode || "0000",
          catgCode: catgCode || "0000",
          isActive: workflow.isActive !== undefined ? workflow.isActive : workflow.IsActive !== undefined ? workflow.IsActive : true,
        });
        
        // Load categories for the workflow's market code
        if (marketCode && marketCode !== "0000") {
          await loadCategories(marketCode);
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load workflow details",
        });
        navigate("/dynamic-approval-configuration");
      }
    } catch (error) {
      console.error("Error loading workflow:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load workflow details",
      });
      navigate("/dynamic-approval-configuration");
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      const response = await approvalWorkflowAPI.getAll();
      if (response?.success && response.data) {
        setWorkflows(response.data);
      } else {
        setWorkflows([]);
      }
    } catch (error) {
      console.error("Error loading workflows:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load approval workflows",
      });
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflowLevels = async (workflowCode) => {
    try {
      const response = await approvalWorkflowAPI.getLevels(workflowCode);
      if (response?.success && response.data) {
        setWorkflowLevels((prev) => ({
          ...prev,
          [workflowCode]: response.data,
        }));
      }
    } catch (error) {
      console.error("Error loading workflow levels:", error);
    }
  };

  const handleAddWorkflow = () => {
    setEditingWorkflow(null);
    setWorkflowForm({
      workflowCode: "",
      workflowName: "",
      srTypeCode: "",
      marketCode: "0000",
      catgCode: "0000",
      isActive: true,
    });
    setCategories([]);
    setShowWorkflowForm(true);
  };

  const handleEditWorkflow = async (workflowCode) => {
    try {
      // Fetch the full workflow details from API to ensure we have all fields
      const response = await approvalWorkflowAPI.get(workflowCode);
      if (response?.success && response.data) {
        const workflow = response.data;
        setEditingWorkflow(workflow);
        const marketCode = workflow.marketCode || workflow.MARKETCODE || "";
        const catgCode = workflow.catgCode || workflow.CATGCODE || "";
        const srTypeCode = workflow.srTypeCode || workflow.SRTYPECODE || "";
        
        setWorkflowForm({
          workflowCode: workflow.workflowCode || workflow.WorkflowCode || "",
          workflowName: workflow.workflowName || workflow.WorkflowName || "",
          srTypeCode: srTypeCode || "",
          marketCode: marketCode || "0000",
          catgCode: catgCode || "0000",
          isActive: workflow.isActive !== undefined ? workflow.isActive : workflow.IsActive !== undefined ? workflow.IsActive : true,
        });
        
        // Load categories for the workflow's market code
        if (marketCode && marketCode !== "0000") {
          await loadCategories(marketCode);
        }
        
        // SR Types will be loaded automatically by the useEffect that watches workflowForm.marketCode
        setShowWorkflowForm(true);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load workflow details",
        });
      }
    } catch (error) {
      console.error("Error loading workflow for edit:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load workflow details",
      });
    }
  };

  const handleDeleteWorkflow = async (workflowCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the workflow and all its levels. This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await approvalWorkflowAPI.delete(workflowCode);
        if (response?.success) {
          Swal.fire("Deleted!", "Workflow has been deleted.", "success");
          loadWorkflows();
        } else {
          Swal.fire("Error!", response?.message || "Failed to delete workflow", "error");
        }
      } catch (error) {
        console.error("Error deleting workflow:", error);
        Swal.fire("Error!", "Failed to delete workflow", "error");
      }
    }
  };

  const handleSaveWorkflow = async () => {
    // Validation
    if (!workflowForm.workflowName.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Workflow Name is required",
      });
      return;
    }

    if (!editingWorkflow && !workflowForm.workflowCode.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Workflow Code is required",
      });
      return;
    }

    try {
      const workflowData = {
        workflowCode: workflowForm.workflowCode,
        workflowName: workflowForm.workflowName,
        SRTYPECODE: workflowForm.srTypeCode && workflowForm.srTypeCode !== "" ? workflowForm.srTypeCode : null,
        MARKETCODE: workflowForm.marketCode && workflowForm.marketCode !== "0000" ? workflowForm.marketCode : null,
        CATGCODE: workflowForm.catgCode && workflowForm.catgCode !== "0000" ? workflowForm.catgCode : null,
        isActive: workflowForm.isActive,
      };

      let response;
      if (editingWorkflow) {
        response = await approvalWorkflowAPI.update(workflowForm.workflowCode, workflowData);
      } else {
        response = await approvalWorkflowAPI.create(workflowData);
      }

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: editingWorkflow ? "Workflow updated successfully" : "Workflow created successfully",
        });
        setShowWorkflowForm(false);
        setEditingWorkflow(null);
        setWorkflowForm({
          workflowCode: "",
          workflowName: "",
          srTypeCode: "",
          marketCode: "0000",
          catgCode: "0000",
          isActive: true,
        });
        setCategories([]);
        if (isAddMode || isEditMode) {
          navigate("/dynamic-approval-configuration");
        } else {
          loadWorkflows();
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to save workflow",
        });
      }
    } catch (error) {
      console.error("Error saving workflow:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save workflow",
      });
    }
  };

  const handleManageLevels = (workflowCode) => {
    navigate(`/dynamic-approval-configuration/levels/${workflowCode}`);
  };

  const handleAddLevel = () => {
    if (!selectedWorkflow) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please select a workflow first",
      });
      return;
    }

    setEditingLevel(null);
    const existingLevels = workflowLevels[selectedWorkflow] || [];
    const maxSequence = existingLevels.length > 0
      ? Math.max(...existingLevels.map((l) => l.levelSequence || l.LevelSequence || 0))
      : 0;

    setLevelForm({
      approvalLevelCode: "",
      levelSequence: maxSequence + 1,
      approvalType: "Required",
      minValue: "",
      maxValue: "",
      currency: "INR",
      plantCode: "",
      userTypeCode: "",
      isPlantQCRequired: false,
      isActive: true,
    });
    setShowLevelForm(true);
  };

  const handleEditLevel = (levelCode) => {
    const levels = workflowLevels[selectedWorkflow] || [];
    const level = levels.find((l) => (l.approvalLevelCode || l.ApprovalLevelCode) === levelCode);
    if (level) {
      setEditingLevel(level);
      setLevelForm({
        approvalLevelCode: level.approvalLevelCode || level.ApprovalLevelCode || "",
        levelSequence: level.levelSequence || level.LevelSequence || 1,
        approvalType: level.approvalType || level.ApprovalType || "Required",
        minValue: level.minValue || level.MinValue || "",
        maxValue: level.maxValue || level.MaxValue || "",
        currency: level.currency || level.Currency || "INR",
        plantCode: level.plantCode || level.PLANTCODE || "",
        userTypeCode: level.userTypeCode || level.USERTYPECODE || "",
        isPlantQCRequired: level.isPlantQCRequired !== undefined
          ? level.isPlantQCRequired
          : level.IsPlantQCRequired !== undefined
          ? level.IsPlantQCRequired
          : false,
        isActive: level.isActive !== undefined
          ? level.isActive
          : level.IsActive !== undefined
          ? level.IsActive
          : true,
      });
      setShowLevelForm(true);
    }
  };

  const handleDeleteLevel = async (levelCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the approval level. This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await approvalWorkflowAPI.deleteLevel(levelCode);
        if (response?.success) {
          Swal.fire("Deleted!", "Approval level has been deleted.", "success");
          loadWorkflowLevels(selectedWorkflow);
        } else {
          Swal.fire("Error!", response?.message || "Failed to delete level", "error");
        }
      } catch (error) {
        console.error("Error deleting level:", error);
        Swal.fire("Error!", "Failed to delete level", "error");
      }
    }
  };

  const handleSaveLevel = async () => {
    // Validation
    if (!levelForm.levelSequence || levelForm.levelSequence < 1) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Level Sequence must be at least 1",
      });
      return;
    }

    if (!editingLevel && !levelForm.approvalLevelCode.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Approval Level Code is required",
      });
      return;
    }

    if (levelForm.approvalType === "Conditional" && (!levelForm.minValue || !levelForm.maxValue)) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Min Value and Max Value are required for Conditional approval type",
      });
      return;
    }

    try {
      const levelData = {
        approvalLevelCode: levelForm.approvalLevelCode,
        workflowCode: selectedWorkflow,
        levelSequence: parseInt(levelForm.levelSequence),
        approvalType: levelForm.approvalType,
        minValue: levelForm.minValue ? parseFloat(levelForm.minValue) : null,
        maxValue: levelForm.maxValue ? parseFloat(levelForm.maxValue) : null,
        currency: levelForm.currency || null,
        plantCode: levelForm.plantCode || null,
        userTypeCode: levelForm.userTypeCode || null,
        isPlantQCRequired: levelForm.isPlantQCRequired,
        isActive: levelForm.isActive,
      };

      let response;
      if (editingLevel) {
        response = await approvalWorkflowAPI.updateLevel(levelForm.approvalLevelCode, levelData);
      } else {
        response = await approvalWorkflowAPI.createLevel(levelData);
      }

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: editingLevel ? "Approval level updated successfully" : "Approval level created successfully",
        });
        setShowLevelForm(false);
        setEditingLevel(null);
        setLevelForm({
          approvalLevelCode: "",
          levelSequence: 1,
          approvalType: "Required",
          minValue: "",
          maxValue: "",
          currency: "INR",
          plantCode: "",
          userTypeCode: "",
          isPlantQCRequired: false,
          isActive: true,
        });
        loadWorkflowLevels(selectedWorkflow);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to save approval level",
        });
      }
    } catch (error) {
      console.error("Error saving level:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save approval level",
      });
    }
  };

  const toggleWorkflowExpansion = (workflowCode) => {
    setExpandedWorkflows((prev) => ({
      ...prev,
      [workflowCode]: !prev[workflowCode],
    }));
    if (!expandedWorkflows[workflowCode]) {
      loadWorkflowLevels(workflowCode);
    }
  };

  if (loading && workflows.length === 0) {
    return (
      <Box className="add-edit-user-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-user-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-user-breadcrumb"
        >
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            Approval Configuration
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-user-header-right">
          <Button
            className="add-edit-user-back-button"
            onClick={() => navigate("/dynamic-approval-configuration")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      <div className="container-fluid add-edit-user-page">

        {/* Workflows Section */}
        <div className="card-panel mb-3">
          <div className="section-heading d-flex align-items-center justify-content-between mb-3">
            <Typography variant="h6" className="add-edit-user-section-heading">
              Approval Workflows
            </Typography>
            {!showWorkflowForm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddWorkflow}
                className="btn-save-user funbtns"
              >
                Add Workflow
              </Button>
            )}
          </div>

          {showWorkflowForm ? (
            <div className="row g-3 mb-4">
              {/* Workflow Code is hidden but still auto-generated */}
              <input type="hidden" value={workflowForm.workflowCode} />
              
              <div className="col-12 col-md-6 col-lg-3">
                <div className="textfield-wrapper">
                  <label className="select2-label pnlLblsze">
                    Workflow Name <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={workflowForm.workflowName}
                    onChange={(e) =>
                      setWorkflowForm({ ...workflowForm, workflowName: e.target.value })
                    }
                    required
                    fullWidth
                    size="small"
                    placeholder="Enter Workflow Name"
                    className="add-edit-user-textfield"
                  />
                </div>
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <div className="textfield-wrapper">
                  <label className="select2-label pnlLblsze">
                    Market <span style={{ color: "red" }}>*</span>
                  </label>
                  <select
                    ref={marketSelectRef}
                    className="form-select form-control-light filter-select text-start"
                    style={{ height: "40px", textAlign: "left" }}
                  >
                    <option value="0000">Select Market</option>
                    {markets && markets.length > 0 ? (
                      markets.map((market) => {
                        const code = market.marketCode || market.MarketCode || "";
                        const name = market.marketName || market.MarketName || "";
                        return (
                          <option key={code} value={code}>
                            {name}
                          </option>
                        );
                      })
                    ) : (
                      <option value="0000" disabled>Loading markets...</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <div className="textfield-wrapper">
                  <label className="select2-label pnlLblsze">
                    Category <span style={{ color: "red" }}>*</span>
                  </label>
                  <select
                    ref={catgSelectRef}
                    className="form-select form-control-light filter-select text-start"
                    style={{ height: "40px", textAlign: "left" }}
                    disabled={!workflowForm.marketCode || workflowForm.marketCode === "0000"}
                  >
                    <option value="0000">
                      {!workflowForm.marketCode || workflowForm.marketCode === "0000" 
                        ? "Please select Market first" 
                        : categories.length === 0 
                          ? "Loading categories..." 
                          : "Select Category"}
                    </option>
                    {categories && categories.length > 0 ? (
                      categories.map((cat) => {
                        const code = cat.catgCode || cat.CatgCode || "";
                        const name = cat.catgName || cat.CatgName || "";
                        return (
                          <option key={code} value={code}>
                            {name}
                          </option>
                        );
                      })
                    ) : null}
                  </select>
                </div>
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <div className="textfield-wrapper">
                  <label className="select2-label pnlLblsze">SR Type</label>
                  <select
                    ref={srTypeSelectRef}
                    className="form-select form-control-light filter-select text-start"
                    style={{ height: "40px", textAlign: "left" }}
                  >
                    <option value="">Select SR Type</option>
                    {srTypeOptions && srTypeOptions.length > 0 ? (
                      srTypeOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.text}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>Loading SR Types...</option>
                    )}
                  </select>
                </div>
              </div>
            </div>
          ) : (
            <>
              {loading ? (
                <div className="text-center py-4">
                  <CircularProgress />
                </div>
              ) : (
                <div className="table-responsive">
                  <table ref={tableRef} className="table table-striped table-hover">
                    <thead></thead>
                    <tbody></tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {showWorkflowForm && (
            <div className="row mt-4">
              <div className="col-12">
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (isAddMode || isEditMode) {
                        navigate("/dynamic-approval-configuration");
                      } else {
                        setShowWorkflowForm(false);
                        setEditingWorkflow(null);
                        setWorkflowForm({
                          workflowCode: "",
                          workflowName: "",
                          srTypeCode: "",
                          marketCode: "0000",
                          catgCode: "0000",
                          isActive: true,
                        });
                        setCategories([]);
                      }
                    }}
                    className="btn-cancel-user funbtns"
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveWorkflow}
                    className="btn-save-user funbtns"
                    startIcon={<SaveIcon />}
                  >
                    Save
                  </Button>
                </Stack>
              </div>
            </div>
          )}
        </div>

        {/* Workflow Levels Section */}
        {selectedWorkflow && (
          <div className="card-panel mb-3">
            <div className="section-heading d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <Typography variant="h6" className="add-edit-user-section-heading me-3">
                  Approval Levels
                </Typography>
                <span className="badge badge-info">
                  Workflow: {workflows.find((w) => w.workflowCode === selectedWorkflow)?.workflowName || selectedWorkflow}
                </span>
              </div>
              {!showLevelForm && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddLevel}
                  className="btn-save-user funbtns"
                >
                  Add Level
                </Button>
              )}
            </div>

            {showLevelForm ? (
              <div className="row g-3 mb-4">
                <div className="col-12 col-md-6 col-lg-3">
                  <div className="textfield-wrapper">
                    <label className="select2-label pnlLblsze">
                      Approval Level Code <span style={{ color: "red" }}>*</span>
                    </label>
                    <TextField
                      value={levelForm.approvalLevelCode}
                      onChange={(e) =>
                        setLevelForm({ ...levelForm, approvalLevelCode: e.target.value })
                      }
                      required
                      fullWidth
                      disabled={!!editingLevel}
                      size="small"
                      placeholder="Enter Approval Level Code"
                      className="add-edit-user-textfield"
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <div className="textfield-wrapper">
                    <label className="select2-label pnlLblsze">
                      Level Sequence <span style={{ color: "red" }}>*</span>
                    </label>
                    <TextField
                      type="number"
                      value={levelForm.levelSequence}
                      onChange={(e) =>
                        setLevelForm({ ...levelForm, levelSequence: parseInt(e.target.value) || 1 })
                      }
                      required
                      fullWidth
                      size="small"
                      placeholder="Enter Level Sequence"
                      className="add-edit-user-textfield"
                      inputProps={{ min: 1 }}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <div className="select2-wrapper">
                    <label className="select2-label pnlLblsze">
                      Approval Type <span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="form-control"
                      value={levelForm.approvalType}
                      onChange={(e) =>
                        setLevelForm({ ...levelForm, approvalType: e.target.value })
                      }
                      required
                    >
                      <option value="Required">Required</option>
                      <option value="Optional">Optional</option>
                      <option value="Conditional">Conditional</option>
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <div className="select2-wrapper">
                    <label className="select2-label pnlLblsze">Currency</label>
                    <select
                      className="form-control"
                      value={levelForm.currency}
                      onChange={(e) =>
                        setLevelForm({ ...levelForm, currency: e.target.value })
                      }
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>
                {levelForm.approvalType === "Conditional" && (
                  <>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="textfield-wrapper">
                        <label className="select2-label pnlLblsze">
                          Min Value <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          type="number"
                          value={levelForm.minValue}
                          onChange={(e) =>
                            setLevelForm({ ...levelForm, minValue: e.target.value })
                          }
                          required
                          fullWidth
                          size="small"
                          placeholder="Enter Min Value"
                          className="add-edit-user-textfield"
                          inputProps={{ step: "0.01" }}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="textfield-wrapper">
                        <label className="select2-label pnlLblsze">
                          Max Value <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          type="number"
                          value={levelForm.maxValue}
                          onChange={(e) =>
                            setLevelForm({ ...levelForm, maxValue: e.target.value })
                          }
                          required
                          fullWidth
                          size="small"
                          placeholder="Enter Max Value"
                          className="add-edit-user-textfield"
                          inputProps={{ step: "0.01" }}
                        />
                      </div>
                    </div>
                  </>
                )}
                <div className="col-12 col-md-6 col-lg-3">
                  <div className="select2-wrapper">
                    <label className="select2-label pnlLblsze">User Type</label>
                    <select
                      className="form-control"
                      value={levelForm.userTypeCode}
                      onChange={(e) =>
                        setLevelForm({ ...levelForm, userTypeCode: e.target.value })
                      }
                    >
                      <option value="">Select User Type</option>
                      {userTypeOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <div className="select2-wrapper">
                    <label className="select2-label pnlLblsze">Plant</label>
                    <select
                      className="form-control"
                      value={levelForm.plantCode}
                      onChange={(e) =>
                        setLevelForm({ ...levelForm, plantCode: e.target.value })
                      }
                    >
                      <option value="">Select Plant</option>
                      {plantOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <div className="select2-wrapper">
                    <label className="select2-label pnlLblsze">Plant QC Required</label>
                    <select
                      className="form-control"
                      value={levelForm.isPlantQCRequired ? "true" : "false"}
                      onChange={(e) =>
                        setLevelForm({ ...levelForm, isPlantQCRequired: e.target.value === "true" })
                      }
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <div className="select2-wrapper">
                    <label className="select2-label pnlLblsze">Status</label>
                    <select
                      className="form-control"
                      value={levelForm.isActive ? "true" : "false"}
                      onChange={(e) =>
                        setLevelForm({ ...levelForm, isActive: e.target.value === "true" })
                      }
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div className="levels-list">
                {workflowLevels[selectedWorkflow] && workflowLevels[selectedWorkflow].length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Sequence</th>
                        <th>Level Code</th>
                        <th>Approval Type</th>
                        <th>Value Range</th>
                        <th>Currency</th>
                        <th>User Type</th>
                        <th>Plant</th>
                        <th>Plant QC Required</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workflowLevels[selectedWorkflow]
                        .sort((a, b) => (a.levelSequence || a.LevelSequence || 0) - (b.levelSequence || b.LevelSequence || 0))
                        .map((level, index) => {
                          const levelCode = level.approvalLevelCode || level.ApprovalLevelCode;
                          const sequence = level.levelSequence || level.LevelSequence || 0;
                          const approvalType = level.approvalType || level.ApprovalType || "";
                          const minValue = level.minValue || level.MinValue;
                          const maxValue = level.maxValue || level.MaxValue;
                          const currency = level.currency || level.Currency || "";
                          const userType = level.userTypeCode || level.USERTYPECODE || "";
                          const plant = level.plantCode || level.PLANTCODE || "";
                          const isPlantQCRequired = level.isPlantQCRequired !== undefined
                            ? level.isPlantQCRequired
                            : level.IsPlantQCRequired !== undefined
                            ? level.IsPlantQCRequired
                            : false;
                          const isActive = level.isActive !== undefined
                            ? level.isActive
                            : level.IsActive !== undefined
                            ? level.IsActive
                            : true;

                          return (
                            <tr key={levelCode || index}>
                              <td>{sequence}</td>
                              <td>{levelCode}</td>
                              <td>
                                <span className={`badge badge-${approvalType === "Required" ? "danger" : approvalType === "Optional" ? "warning" : "info"}`}>
                                  {approvalType}
                                </span>
                              </td>
                              <td>
                                {minValue !== null && minValue !== undefined && maxValue !== null && maxValue !== undefined
                                  ? `${minValue} - ${maxValue}`
                                  : "-"}
                              </td>
                              <td>{currency || "-"}</td>
                              <td>
                                {userTypeOptions.find((u) => u.id === userType)?.text || userType || "-"}
                              </td>
                              <td>
                                {plantOptions.find((p) => p.id === plant)?.text || plant || "-"}
                              </td>
                              <td>
                                <span className={`badge ${isPlantQCRequired ? "badge-success" : "badge-secondary"}`}>
                                  {isPlantQCRequired ? "Yes" : "No"}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${isActive ? "badge-success" : "badge-danger"}`}>
                                  {isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    className="btn btn-sm btn-edit"
                                    onClick={() => handleEditLevel(levelCode)}
                                    title="Edit"
                                  >
                                    <EditIcon fontSize="small" />
                                  </button>
                                  <button
                                    className="btn btn-sm btn-delete"
                                    onClick={() => handleDeleteLevel(levelCode)}
                                    title="Delete"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              ) : (
                  <div className="text-center py-4 text-muted">
                    No approval levels configured. Click "Add Level" to create one.
                  </div>
                )}
              </div>
            )}
            {showLevelForm && (
              <div className="row mt-4">
                <div className="col-12">
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="contained"
                      onClick={() => {
                        setShowLevelForm(false);
                        setEditingLevel(null);
                        setLevelForm({
                          approvalLevelCode: "",
                          levelSequence: 1,
                          approvalType: "Required",
                          minValue: "",
                          maxValue: "",
                          currency: "INR",
                          plantCode: "",
                          userTypeCode: "",
                          isPlantQCRequired: false,
                          isActive: true,
                        });
                      }}
                      className="btn-cancel-user funbtns"
                      startIcon={<CancelIcon />}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveLevel}
                      className="btn-save-user funbtns"
                      startIcon={<SaveIcon />}
                    >
                      Save
                    </Button>
                  </Stack>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </Box>
  );
};

export default DynamicApprovalConfiguration;
